import {Injectable, OnDestroy, OnInit} from '@angular/core';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {AppContainerService, PersonalizationService, SideNavBarService} from '@mydocma/app-container';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, combineLatest, firstValueFrom, map} from 'rxjs';
import {MmPlanEditService} from '../../mm-plans-view/mm-plan-view/services/mm-plan-edit.service';
import {MmPlansManagerService} from '../../mm-plans-view/services/mm-plans-manager.service';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {MmViewService} from '../../../mm-view.service';
import {ActivatedRoute} from '@angular/router';
import {MmProjectLoaderService} from '../../../mm-project/mm-project-loader.service';
import {BpApiService} from '../../../bp-common/bp-api.service';
import {Logger} from '@mydocma/common/src/lib/logger';
import {MmProjectOfflineService} from '../../../mm-project/mm-project-offline.service';
import {filter, first} from 'rxjs/operators';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {MmFaultsViewService} from './mm-faults-view.service';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {SelectionService} from '@mydocma/component-library/src/lib/selection';
import {MmPlanPinService} from '../../mm-plans-view/mm-plan-view/services/mm-plan-pin.service';
import {Plan} from '@docma-mm-core/plan/plan';

export enum HeaderToggleMenuEnum {
  gridView = 'gridView',
  tileView = 'tileView',
  planView = 'planView'
}

@UntilDestroy()
@Injectable()
export class MmFaultsSplitService implements OnInit, OnDestroy {

  private toggledLeftViewsSubject = new BehaviorSubject<any[]>([]);
  toggledLeftViews$ = this.toggledLeftViewsSubject.asObservable();

  private toggledRightViewsSubject = new BehaviorSubject<any[]>([]);
  toggledRightViews$ = this.toggledRightViewsSubject.asObservable();

  previousToggleValue: string;
  currentToggleValue: string;
  toggledLeftViews = [];
  toggledRightViews = [];
  leftAreaSize: any = 50;
  rightAreaSize: any = 50;
  readonly planAreaMinWidth = 255;
  layoutSettingsInitialized = false;

  faultsViewService: MmFaultsViewService;
  planPinService: MmPlanPinService;

  // after plan closed saved value will be set ot toggle group
  public lastToggleViewBeforePlanSelected: HeaderToggleMenuEnum;
  private rightViewVisibleSubject = new BehaviorSubject<boolean>(false);

  // used in mm fault header comp to check last toggle state before plan view selected in tablet
  rightViewVisible$ = this.rightViewVisibleSubject.asObservable();

  constructor(private readonly personalizationService: PersonalizationService,
              private readonly plansManagerService: MmPlansManagerService,
              private readonly planEditSerivce: MmPlanEditService,
              private sideNavBarService: SideNavBarService,
              private mmViewService: MmViewService,
              public deviceService: DeviceService,
              private readonly activatedRoute: ActivatedRoute,
              private readonly appContainerService: AppContainerService,
              private readonly loaderService: MmProjectLoaderService,
              private readonly offlineService: MmProjectOfflineService,
              private readonly bpApiService: BpApiService,
              public readonly selectionService: SelectionService<ViewFault>,
              private readonly logger: Logger) {

    this.planEditSerivce.attachTo(this);

    if (this.appContainerService.isMobileView) {
      this.setLeftButtonViewValue([]);
      this.toggledLeftViews = [HeaderToggleMenuEnum.tileView];
      this.toggledLeftViewsSubject.next(this.toggledLeftViews);
    }

    this.loaderService.project
      .pipe(untilDestroyed(this), filter(x => x.loadState.status === LoadStatusEnum.Loaded))
      .subscribe(() => {
        this.initialize();
      });
  }

  attachTo(targetToAttach: any) {
    if (targetToAttach instanceof MmFaultsViewService) {
      this.faultsViewService = targetToAttach;
    } else if (targetToAttach instanceof MmPlanPinService) {
      this.planPinService = targetToAttach;
    }
  }

  get rightViewVisible(): boolean {
    return this.rightViewVisibleSubject.value;
  }


  ngOnInit() {

  }

  ngOnDestroy(): void {
  }

  public toggleView(value: HeaderToggleMenuEnum): void {


    if (value === HeaderToggleMenuEnum.planView) {
      this.togglePlanView();
    } else {
      this.toggledLeftViews = [value];
      this.toggledLeftViewsSubject.next(this.toggledLeftViews);
    }
    this.manageToggleState(value);

    this.saveLayoutSettings();
  }


  public saveAreaSize({gutterNum, sizes}): void {
    this.leftAreaSize = sizes[0];
    this.rightAreaSize = sizes[1];
    this.saveLayoutSettings();
  }

  private manageToggleState(value): void {
    if (value !== this.currentToggleValue) {
      this.previousToggleValue = this.currentToggleValue;
      this.currentToggleValue = value;
    }
  }

  private async saveLayoutSettings() {
    await this.personalizationService.setValue(PersonalizationKeysEnum.Fault_PlanSplit_LeftViewSize, this.leftAreaSize);
    await this.personalizationService.setValue(PersonalizationKeysEnum.Fault_PlanSplit_RightViewSize, this.rightAreaSize);
    await this.personalizationService.setValue(PersonalizationKeysEnum.Fault_LeftToggleGroup, this.toggledLeftViews);
    await this.personalizationService.setValue(PersonalizationKeysEnum.Fault_RightToggleGroup, this.toggledRightViews);
  }

  private async loadLayoutSettings() {
    await firstValueFrom(combineLatest([
      this.personalizationService.getValue(PersonalizationKeysEnum.Fault_PlanSplit_LeftViewSize),
      this.personalizationService.getValue(PersonalizationKeysEnum.Fault_PlanSplit_RightViewSize),
      this.personalizationService.getValue(PersonalizationKeysEnum.Fault_LeftToggleGroup),
      this.personalizationService.getValue(PersonalizationKeysEnum.Fault_RightToggleGroup)
    ]).pipe(untilDestroyed(this),
      map(async ([leftAreaSizeLoaded, rightAreaSizeLoaded, toggledLeftViewsLoaded, toggledRightViewsLoaded]) => {
        if (!this.appContainerService.isMobileView && !toggledLeftViewsLoaded) {
          const project = await firstValueFrom(this.loaderService.project.pipe(map(x => x.data)));

          const plansCount = await firstValueFrom(this.loaderService.plans.pipe(filter(x => x.loadState.status === LoadStatusEnum.Loaded), map(x => (x?.data?.length ?? 0))));

          if (plansCount > 0) {
            this.setLeftButtonViewValue([HeaderToggleMenuEnum.tileView]);
            this.setRightButtonViewValue([HeaderToggleMenuEnum.planView]);
            this.saveLayoutSettings();
          } else {
            this.setLeftButtonViewValue(toggledLeftViewsLoaded);
            this.setRightButtonViewValue(toggledRightViewsLoaded);
          }
        } else {
          this.setLeftButtonViewValue(toggledLeftViewsLoaded);
          this.setRightButtonViewValue(toggledRightViewsLoaded);
        }

        this.leftAreaSize = (leftAreaSizeLoaded ? leftAreaSizeLoaded : this.leftAreaSize);
        this.rightAreaSize = (rightAreaSizeLoaded ? rightAreaSizeLoaded : this.rightAreaSize);
        this.setRightViewVisibility();
      }))).catch((e) => {
      this.logger.error(`an error ${e} occurred while loading layout configurations`, '', 'MmFaultsSplitService');
    });
  }

  private async initialize() {
    if (!this.appContainerService.isMobileView) {
      if (!this.layoutSettingsInitialized) {
        this.layoutSettingsInitialized = true;
        await this.loadLayoutSettings();
      }
    }
  }

  public async disablePlanView() {
    if (!this.appContainerService.isMobileView) {
      const leftToggleGroup = await this.personalizationService.getValue(PersonalizationKeysEnum.Fault_LeftToggleGroup);
      if (!!leftToggleGroup) {
        this.toggledRightViews = [];
        await this.setRightViewVisble(false);
        this.saveLayoutSettings();
      }
    }
  }

  public async setRightViewVisble(visible: boolean) {
    if (visible) {
      this.toggledRightViews = [HeaderToggleMenuEnum.planView];
      if (this.appContainerService.isMobileView) {
        this.toggledLeftViews = [];
        this.toggledLeftViewsSubject.next(this.toggledLeftViews);
      }
    } else {
      this.toggledRightViews = [];
      if (this.appContainerService.isMobileView) {
        this.toggledLeftViews = [HeaderToggleMenuEnum.tileView];
        this.toggledLeftViewsSubject.next(this.toggledLeftViews);
      }

      if (!this.toggledLeftViews?.length) {
        this.toggledLeftViews = [this.previousToggleValue];
        this.toggledLeftViewsSubject.next(this.toggledLeftViews);
      }

      await this.plansManagerService.setSelectedPlan(null);
    }

    this.rightViewVisibleSubject.next(visible);
  }

  private setRightButtonViewValue(toggledRightViewsLoaded: string[]) {
    this.toggledRightViews = toggledRightViewsLoaded;
    this.toggledRightViewsSubject.next(this.toggledRightViews);
  }

  private setLeftButtonViewValue(toggledLeftViewsLoaded: string[]) {
    // create
    const isMobile = this.appContainerService.isMobileView;
    const defaultValue = isMobile ? [HeaderToggleMenuEnum.tileView] : [HeaderToggleMenuEnum.gridView];
    const isLeftViewContainsInitialValue = toggledLeftViewsLoaded?.some(x => x === HeaderToggleMenuEnum.gridView || x === HeaderToggleMenuEnum.tileView);
    if (!isLeftViewContainsInitialValue || isMobile) {
      this.toggledLeftViews = defaultValue;
      this.toggledLeftViewsSubject.next(this.toggledLeftViews);
    } else {
      this.toggledLeftViews = toggledLeftViewsLoaded;
      this.toggledLeftViewsSubject.next(this.toggledLeftViews);
    }

    this.previousToggleValue = this.toggledLeftViews[0];
    this.currentToggleValue = this.toggledLeftViews[0];
  }

  private setRightViewVisibility() {
    if (!this.toggledRightViews?.length) {
      this.setRightViewVisble(false);
    } else {
      this.setRightViewVisble(true);
    }
  }


  // }

  // open or close right panel with plan
  // changes state of header toggle buttons depending on device type
  private async togglePlanView(skipPlanSelection = false) {
    if (this.toggledLeftViews.length === 0) {
      this.toggledLeftViews.push(HeaderToggleMenuEnum.gridView);
      this.toggledLeftViewsSubject.next(this.toggledLeftViews);
    }
    if (this.toggledRightViews.length > 0) {
      await this.setRightViewVisble(false);
    } else {
      this.plansManagerService.setPlanFlyoutVisible(false);
      await this.setRightViewVisble(true);
      const currentFault = await firstValueFrom(this.faultsViewService.currentFault.pipe(first()));
      // const selectedItems = await firstValueFrom(this.selectionService.selectedItems);

      if (this.plansManagerService.plansByStructure.length > 0 && !skipPlanSelection && !currentFault) {
        this.plansManagerService.setSelectedPlan(this.plansManagerService.plansByStructure[0], true);
      } else if (this.plansManagerService.plansByStructure.length > 0 && !skipPlanSelection && !!currentFault) {
        const pinsDict = await firstValueFrom(this.planPinService.plansWithPins$.pipe(
          map((plans: Plan[]) => {
            const pinDict: { [key: string|number]: Plan } = {};
            for (const plan of plans) {
              for (const pin of plan.pins) {
                pinDict[pin.fault.identifier ?? pin.fault.uid] = plan;
              }
            }
            return pinDict;
          })
        ));

        const onPlan = (pinsDict.hasOwnProperty((currentFault?.identifier ?? currentFault?.uid)) ? pinsDict[(currentFault?.identifier ?? currentFault?.uid)] : null);
        if ((!onPlan || this.plansManagerService.plansByStructure[0].id === onPlan?.id)) {
          this.plansManagerService.setSelectedPlan(this.plansManagerService.plansByStructure[0], true);
        }
      }
    }
  }

}
